import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ColDef, GetRowIdParams, GridReadyEvent } from "ag-grid-community";
import { IServerSideDatasource } from "ag-grid-community";
import { createGraphQLClientWithMiddleware } from "services/graphqlClient";
import { DELIVERY_TRIP_ITEM_LIST_VIEW } from "services/AgGrid/DeliveryTripAgGrid";
import { dateFilterModel } from "utils/AgGridFilter";
import { useDeliveryTripItemListColumnDefs } from "components/Table/Sales/BillingNote/DeliveryTripItemList/columnDefs";
import { AgGridReact } from "ag-grid-react";
import CheckboxAggridModalSS from "./CheckboxAggridSSModal";
import { DT_STATUS_ALL } from "types/Logistic/deliveryTripStatus";

type CheckedDeliveryTripItemListModalProps = {
  showSelectDeliveryTripItemList: boolean;
  closeDeliveryTripItemListTable: () => void;
  addDeliveryTripsItemListHandler: (data: any, scopeIds?: Set<string>) => void;
  deliveryTripItemListIds: string[];
  setDeliveryTripItemListIds: Dispatch<SetStateAction<string[]>>;
  deliveryTripItemListIdsSnapshot: string[];
  setDeliveryTripItemListIdsSnapshot: Dispatch<SetStateAction<string[]>>;
  rowData?: any[];
  rowSelection?: "single" | "multiple";
  customerUniqueId?: string;
  billingNoteUniqueId?: string;
};

const CheckedDeliveryTripItemListModal = ({
  showSelectDeliveryTripItemList,
  closeDeliveryTripItemListTable,
  addDeliveryTripsItemListHandler,
  deliveryTripItemListIds,
  setDeliveryTripItemListIds,
  deliveryTripItemListIdsSnapshot,
  setDeliveryTripItemListIdsSnapshot,
  rowSelection = "multiple",
}: CheckedDeliveryTripItemListModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const selectGridRef = useRef<AgGridReact>();
  const filterModelRef = useRef<any>({});

  // Keep a ref so the datasource closure always sees the latest selectedIds
  // without needing to be recreated when they change.
  const selectedIdsRef = useRef<string[]>(deliveryTripItemListIds);
  useEffect(() => {
    selectedIdsRef.current = deliveryTripItemListIds;
  }, [deliveryTripItemListIds]);

  // Stable GQL client — created once, never changes.
  const graphQLClientWithHeaderDeliveryTrip = useMemo(
    () => createGraphQLClientWithMiddleware("sales"),
    [],
  );

  const columnDefs: ColDef[] = useDeliveryTripItemListColumnDefs();

  // Stable datasource — memoized so setServerSideDatasource is only called once.
  // Uses refs (filterModelRef, selectedIdsRef) so it always sees the latest
  // values without needing to be recreated.
  const datasource: IServerSideDatasource = useMemo(
    () => ({
      async getRows(params) {
        const { request } = params;
        const { startRow, endRow, filterModel, sortModel } = request;

        const { dt_issue_date, dt_aggrid_status, ...otherFilter } = filterModel;

        const aggridStatusValues = dt_aggrid_status
          ? dt_aggrid_status.values.length > 0
            ? Array.from(new Set([...dt_aggrid_status.values, "late"]))
            : []
          : DT_STATUS_ALL;

        const mainStatusValues = aggridStatusValues.filter(
          (v: string) => !["late", "cancelled"].includes(v),
        );

        const formatFilter = {
          ...otherFilter,
          dt_issue_date: dateFilterModel(dt_issue_date),
          dt_aggrid_status: { values: aggridStatusValues, filterType: "set" },
          dt_main_status: {
            values: mainStatusValues.length ? mainStatusValues : ["finished"],
            filterType: "set",
          },
        };

        filterModelRef.current = formatFilter;

        try {
          const { DeliveryTripItemListViewsAggrid } =
            await graphQLClientWithHeaderDeliveryTrip.request(
              DELIVERY_TRIP_ITEM_LIST_VIEW,
              {
                aggridInput: {
                  startRow,
                  endRow,
                  filterModel: formatFilter,
                  sortModel,
                },
              },
            );

          const results = DeliveryTripItemListViewsAggrid.results as any[];

          params.success({
            rowData: results,
            rowCount: DeliveryTripItemListViewsAggrid.count as number,
          });

          // Apply pre-selections AFTER the block is rendered.
          // Using setTimeout(0) puts this outside AG Grid's rendering cycle,
          // preventing SSRM from treating the setSelected call as a data change
          // that would trigger an infinite re-fetch loop.
          setTimeout(() => {
            results.forEach((item: any) => {
              if (selectedIdsRef.current.includes(item.income_unique_id)) {
                const node = params.api.getRowNode(
                  `${item.income_unique_id}_${item.dt_unique_id}`,
                );
                if (node && !node.isSelected()) {
                  node.setSelected(true, false, "api");
                }
              }
            });
          }, 0);
        } catch (err) {
          console.error("Error fetching data:", err);
          params.fail();
        }
      },
    }),
    [graphQLClientWithHeaderDeliveryTrip],
  );

  // Stable onGridReady — memoized so handleGridReady in CheckboxAggridSSModal
  // doesn't change on every parent re-render.
  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      params.api.setFilterModel({
        dt_aggrid_status: { filterType: "set", values: ["finished"] },
      });

      params.api.setServerSideDatasource(datasource);
    },
    [datasource],
  );

  const getRowId = useCallback((params: GetRowIdParams) => {
    return `${params.data.income_unique_id}_${params.data.dt_unique_id}`;
  }, []);

  const finishDeliveryTripsItemListSelect = async (
    isSelectAll: boolean,
    _selectedData: any,
    excludedIds: string[] = [],
  ) => {
    setIsLoading(true);
    const { DeliveryTripItemListViewsAggrid } =
      await graphQLClientWithHeaderDeliveryTrip.request(
        DELIVERY_TRIP_ITEM_LIST_VIEW,
        {
          aggridInput: {
            startRow: 0,
            endRow: 1000000,
            filterModel: filterModelRef.current,
          },
        },
      );

    const allScopeItems = DeliveryTripItemListViewsAggrid.results as any[];
    const scopeIncomeIds = new Set(
      allScopeItems.map((item) => item.income_unique_id),
    );

    let selectedItems: any[];
    if (isSelectAll) {
      if (excludedIds.length > 0) {
        // Select-all with some rows deselected — exclude those from the result.
        const excludedSet = new Set(excludedIds);
        selectedItems = allScopeItems.filter(
          (item) => !excludedSet.has(item.income_unique_id),
        );
      } else {
        selectedItems = allScopeItems;
      }
    } else {
      // Filter the full server result by the tracked selection IDs so that
      // deselected items are excluded even if they were on unloaded pages.
      const selectedIdSet = new Set(deliveryTripItemListIds);
      selectedItems = allScopeItems.filter((item) =>
        selectedIdSet.has(item.income_unique_id),
      );
    }

    // Pass scopeIncomeIds so the parent can do a scope-aware merge:
    // items outside the current filter are preserved, not replaced.
    addDeliveryTripsItemListHandler(selectedItems, scopeIncomeIds);
    setIsLoading(false);
  };

  return (
    <CheckboxAggridModalSS
      modalTitle={t("logistic.delivery_trip.index")}
      cancelBtnTitle={t("button.cancel")}
      btnTitle={t("button.confirm")}
      gridRef={selectGridRef}
      height={500}
      columnDefs={columnDefs}
      rowSelection={rowSelection}
      onFinishEditing={finishDeliveryTripsItemListSelect}
      modalIsOpen={showSelectDeliveryTripItemList}
      getRowId={getRowId}
      closeModal={closeDeliveryTripItemListTable}
      selectedIds={deliveryTripItemListIds}
      setSelectedIds={setDeliveryTripItemListIds}
      idsSnapshot={deliveryTripItemListIdsSnapshot}
      setIdsSnapshot={setDeliveryTripItemListIdsSnapshot}
      isLoading={isLoading}
      onGridReady={onGridReady}
      rowMultiSelectWithClick={true}
      keyName="income_unique_id"
    />
  );
};

export default CheckedDeliveryTripItemListModal;
